import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  Copy,
  Mail,
  Plus,
  Search,
  ShieldCheck,
  Trash2,
  UserMinus,
  UserPlus,
  Users2,
} from "lucide-react";
import TopBar from "@/components/TopBar";
import { Config, DealAccessApi, Deals, Invitations, Organizations, Users } from "@/api/endpoints";
import type { Stage, User } from "@/api/types";
import { useAuthStore } from "@/store/auth";
import { apiErrorMessage } from "@/api/client";
import { confirmDialog, toast } from "@/lib/feedback";

export default function AdminPage() {
  const me = useAuthStore((s) => s.user);
  const isAdmin = me?.role === "admin"; // route is already gated, but keep for card props
  const isPlatformAdmin = !!me?.is_platform_admin;
  return (
    <>
      <TopBar title="Admin" />
      <div className="px-4 py-4 md:px-6 md:py-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <UsersCard isPlatformAdmin={isPlatformAdmin} />
        <StagesCard isAdmin={isAdmin} />
        {isPlatformAdmin && <OrganizationsCard />}
      </div>
    </>
  );
}

// ─── Users / invitations (admin) ──────────────────────────────

function UsersCard({ isPlatformAdmin }: { isPlatformAdmin: boolean }) {
  const qc = useQueryClient();
  const usersQuery = useQuery({ queryKey: ["users"], queryFn: () => Users.list() });
  const invitesQuery = useQuery({ queryKey: ["invitations"], queryFn: () => Invitations.list() });
  const [form, setForm] = useState({ email: "", role: "user" as "user" | "admin" });
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [emailedTo, setEmailedTo] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [assignUser, setAssignUser] = useState<User | null>(null);

  const createMutation = useMutation({
    mutationFn: () => Invitations.create({ email: form.email.trim(), role: form.role }),
    onSuccess: (inv) => {
      qc.invalidateQueries({ queryKey: ["invitations"] });
      setForm({ email: "", role: "user" });
      // The backend returns a token ONLY as a dev fallback (no SMTP). When present,
      // surface the link to share manually; otherwise an email was actually sent.
      const link = inv.token ? `${window.location.origin}/invite/${inv.token}` : null;
      setInviteLink(link);
      setEmailedTo(link ? null : inv.email);
      setCopied(false);
    },
    onError: (e) => toast.err(apiErrorMessage(e)),
  });
  const revokeMutation = useMutation({
    mutationFn: (id: string) => Invitations.revoke(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["invitations"] }),
  });
  const deactivateMutation = useMutation({
    mutationFn: (id: string) => Users.deactivate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
  const activateMutation = useMutation({
    mutationFn: (id: string) => Users.activate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });

  const copyLink = async () => {
    if (!inviteLink) return;
    await navigator.clipboard.writeText(inviteLink);
    setCopied(true);
  };

  const pending = (invitesQuery.data ?? []).filter((i) => i.status === "pending");

  return (
    <section className="panel p-5">
      <h2 className="section-title mb-4">Team</h2>

      <div className="flex gap-2 mb-3">
        <input
          className="input flex-1 min-w-0"
          placeholder="Invite by email…"
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          onKeyDown={(e) =>
            e.key === "Enter" &&
            form.email.trim() &&
            !createMutation.isPending &&
            createMutation.mutate()
          }
        />
        <select
          className="input w-28 shrink-0"
          value={form.role}
          onChange={(e) => setForm({ ...form, role: e.target.value as "user" | "admin" })}
        >
          <option value="user">user</option>
          <option value="admin">admin</option>
        </select>
        <button
          className="btn btn-primary"
          disabled={!form.email.trim() || createMutation.isPending}
          onClick={() => createMutation.mutate()}
        >
          <Mail className="w-4 h-4" /> Invite
        </button>
      </div>

      {inviteLink && (
        <div className="panel-tight p-3 mb-4">
          <div className="text-xs text-mut-300 mb-1.5">
            No email was sent — share this accept link:
          </div>
          <div className="flex items-center gap-2">
            <code className="text-xs text-accent-azure truncate flex-1">{inviteLink}</code>
            <button className="btn btn-ghost" onClick={copyLink}>
              {copied ? (
                <Check className="w-4 h-4 text-accent-azure" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      )}

      {emailedTo && (
        <div className="panel-tight p-3 mb-4 flex items-center gap-2 text-sm text-accent-azure">
          <Check className="w-4 h-4 shrink-0" />
          <span>
            Invitation emailed to <span className="font-medium">{emailedTo}</span>.
          </span>
        </div>
      )}

      {pending.length > 0 && (
        <div className="mb-4">
          <div className="text-xs text-mut-300 mb-1.5">Pending invitations</div>
          <div className="space-y-2">
            {pending.map((inv) => (
              <div key={inv.id} className="panel-tight p-3 flex items-center justify-between">
                <div className="min-w-0">
                  <div className="text-md truncate">{inv.email}</div>
                  <div className="text-xs text-mut-300 mt-0.5">
                    {inv.role} · expires {new Date(inv.expires_at).toLocaleDateString()}
                  </div>
                </div>
                <button
                  className="btn btn-ghost text-accent-pink"
                  onClick={async () => {
                    if (
                      await confirmDialog({
                        title: `Revoke the invitation for ${inv.email}?`,
                        body: "Their invite link stops working.",
                        confirmLabel: "Revoke",
                        danger: true,
                      })
                    )
                      revokeMutation.mutate(inv.id);
                  }}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-2">
        {(usersQuery.data ?? []).map((u) => (
          <div
            key={u.id}
            className="panel-tight p-3 flex items-center justify-between cursor-pointer hover:bg-fg/[0.03] transition-colors"
            role="button"
            tabIndex={0}
            title="Assign deals"
            onClick={() => setAssignUser(u)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setAssignUser(u);
              }
            }}
          >
            <div className="min-w-0">
              <div className="text-md truncate">
                {u.name} <span className="text-mut-300">· {u.email}</span>
              </div>
              <div className="text-xs text-mut-300 flex items-center gap-1.5 mt-0.5">
                {u.role === "admin" && <ShieldCheck className="w-3 h-3 text-accent-azure" />}{" "}
                {u.role} ·{u.is_active ? " active" : " inactive"}
                {isPlatformAdmin && u.is_platform_admin && " · platform admin"}
                {u.must_change_password && " · must change password"}
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                className="btn btn-ghost text-mut-300"
                title="Assign deals"
                onClick={(e) => {
                  e.stopPropagation();
                  setAssignUser(u);
                }}
              >
                <Users2 className="w-4 h-4" />
              </button>
              {u.is_active ? (
                <button
                  className="btn btn-ghost text-accent-pink"
                  title="Deactivate user"
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (
                      await confirmDialog({
                        title: `Deactivate ${u.email}?`,
                        body: "They can't sign in until you reactivate them.",
                        confirmLabel: "Deactivate",
                        danger: true,
                      })
                    )
                      deactivateMutation.mutate(u.id);
                  }}
                >
                  <UserMinus className="w-4 h-4" />
                </button>
              ) : (
                <button
                  className="btn btn-ghost text-accent-azure"
                  title="Activate user"
                  onClick={(e) => {
                    e.stopPropagation();
                    activateMutation.mutate(u.id);
                  }}
                >
                  <UserPlus className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {assignUser && <AssignDealsModal user={assignUser} onClose={() => setAssignUser(null)} />}
    </section>
  );
}

// ─── Assign deals to a user (search / tick / owner-viewer / sync) ──────

type RowState = {
  checked: boolean;
  capacity: "owner" | "viewer";
  wasGranted: boolean;
  origCapacity?: "owner" | "viewer";
  origIsPrimary?: boolean;
};

function AssignDealsModal({ user, onClose }: { user: User; onClose: () => void }) {
  const qc = useQueryClient();
  const dealsQuery = useQuery({ queryKey: ["deals"], queryFn: () => Deals.list() });
  const grantsQuery = useQuery({
    queryKey: ["user-deal-access", user.id],
    queryFn: () => Users.dealAccess(user.id),
  });

  const [rows, setRows] = useState<Map<string, RowState>>(new Map());
  const [initialized, setInitialized] = useState(false);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  const deals = dealsQuery.data;
  const grants = grantsQuery.data;

  // Seed local state once both queries have loaded — pre-tick current grants.
  useEffect(() => {
    if (initialized || !deals || !grants) return;
    const byDeal = new Map(grants.map((g) => [g.deal_id, g]));
    const next = new Map<string, RowState>();
    for (const d of deals) {
      const g = byDeal.get(d.id);
      next.set(
        d.id,
        g
          ? {
              checked: true,
              capacity: g.capacity,
              wasGranted: true,
              origCapacity: g.capacity,
              origIsPrimary: g.is_primary,
            }
          : { checked: false, capacity: "viewer", wasGranted: false },
      );
    }
    setRows(next);
    setInitialized(true);
  }, [initialized, deals, grants]);

  // Escape closes the modal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const setRow = (dealId: string, patch: Partial<RowState>) =>
    setRows((m) => {
      const cur = m.get(dealId);
      if (!cur) return m;
      const next = new Map(m);
      next.set(dealId, { ...cur, ...patch });
      return next;
    });

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (deals ?? []).filter((d) => !term || (d.lead || "").toLowerCase().includes(term));
  }, [deals, q]);

  const checkedCount = useMemo(() => [...rows.values()].filter((r) => r.checked).length, [rows]);

  const save = async () => {
    if (!deals) return;
    setSaving(true);
    try {
      const ops: Promise<unknown>[] = [];
      for (const d of deals) {
        const r = rows.get(d.id);
        if (!r) continue;
        if (r.checked && !r.wasGranted) {
          // Newly granted.
          ops.push(
            DealAccessApi.grant(d.id, {
              user_id: user.id,
              capacity: r.capacity,
              is_primary: false,
            }),
          );
        } else if (r.checked && r.wasGranted && r.capacity !== r.origCapacity) {
          // Capacity changed — preserve the original is_primary so we don't demote a primary owner.
          ops.push(
            DealAccessApi.grant(d.id, {
              user_id: user.id,
              capacity: r.capacity,
              is_primary: r.origIsPrimary ?? false,
            }),
          );
        } else if (!r.checked && r.wasGranted) {
          // Revoked.
          ops.push(DealAccessApi.revoke(d.id, user.id));
        }
        // Unchanged → skip.
      }
      await Promise.all(ops);
      qc.invalidateQueries({ queryKey: ["user-deal-access", user.id] });
      qc.invalidateQueries({ queryKey: ["deals"] });
      onClose();
    } catch (e) {
      toast.err(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const loading = dealsQuery.isLoading || grantsQuery.isLoading || !initialized;

  if (typeof document === "undefined") return null;

  const overlay = (
    <>
      <div className="cmdk-backdrop" onClick={onClose} />
      <div
        className="cmdk"
        role="dialog"
        aria-modal="true"
        aria-label={`Assign deals to ${user.name}`}
      >
        <div className="px-4 pt-4 pb-3 border-b border-fg/[0.06]">
          <h3 className="text-md font-semibold">
            Assign deals · <span className="text-mut-300">{user.name}</span>
          </h3>
          <div className="flex items-center gap-2 mt-3">
            <Search className="w-4 h-4 text-mut-300 shrink-0" />
            <input
              className="input flex-1 min-w-0"
              placeholder="Search deals by lead…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              autoFocus
            />
          </div>
        </div>

        <div className="cmdk-list">
          {loading ? (
            <div className="py-8 px-4 text-center text-mut-300 text-md">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="py-8 px-4 text-center text-mut-300 text-md">
              {(deals ?? []).length === 0
                ? "No deals in this organization."
                : `No deals match "${q}".`}
            </div>
          ) : (
            <div className="space-y-1.5 px-2 py-2">
              {filtered.map((d) => {
                const r = rows.get(d.id);
                if (!r) return null;
                return (
                  <label
                    key={d.id}
                    className="panel-tight p-2.5 flex items-center gap-2 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      className="shrink-0"
                      checked={r.checked}
                      onChange={(e) => setRow(d.id, { checked: e.target.checked })}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-md truncate">{d.lead}</div>
                      <div className="text-xs text-mut-300 truncate">{d.stage}</div>
                    </div>
                    <select
                      className="input w-28 shrink-0"
                      value={r.capacity}
                      disabled={!r.checked}
                      onClick={(e) => e.preventDefault()}
                      onChange={(e) =>
                        setRow(d.id, { capacity: e.target.value as "owner" | "viewer" })
                      }
                    >
                      <option value="owner">owner</option>
                      <option value="viewer">viewer</option>
                    </select>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 px-4 py-3 border-t border-fg/[0.06]">
          <span className="text-xs text-mut-300">{checkedCount} selected</span>
          <div className="ml-auto flex items-center gap-2">
            <button className="btn btn-ghost" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={save} disabled={saving || loading}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(overlay, document.body);
}

// ─── Stages (admin-editable) ──────────────────────────────────

function StagesCard({ isAdmin }: { isAdmin: boolean }) {
  const qc = useQueryClient();
  const stagesQuery = useQuery({ queryKey: ["stages"], queryFn: () => Config.listStages() });
  const [form, setForm] = useState({ name: "", color: "", is_terminal: false });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["stages"] });

  const createMutation = useMutation({
    mutationFn: () =>
      Config.createStage({
        name: form.name.trim(),
        color: form.color.trim() || null,
        is_terminal: form.is_terminal,
      }),
    onSuccess: () => {
      setForm({ name: "", color: "", is_terminal: false });
      invalidate();
    },
    onError: (e) => toast.err(apiErrorMessage(e)),
  });
  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<Stage> }) =>
      Config.updateStage(id, payload),
    onSuccess: invalidate,
    onError: (e) => toast.err(apiErrorMessage(e)),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => Config.deleteStage(id),
    onSuccess: invalidate,
    onError: (e) => toast.err(apiErrorMessage(e)),
  });

  const stages = [...(stagesQuery.data ?? [])].sort((a, b) => a.position - b.position);

  // Reorder by swapping positions with the neighbour in the requested direction.
  const move = (idx: number, dir: -1 | 1) => {
    const target = stages[idx + dir];
    const cur = stages[idx];
    if (!target || !cur) return;
    updateMutation.mutate({ id: cur.id, payload: { position: target.position } });
    updateMutation.mutate({ id: target.id, payload: { position: cur.position } });
  };

  return (
    <section className="panel p-5">
      <h2 className="section-title mb-4">Stages</h2>

      {isAdmin && (
        <div className="flex gap-2 mb-4 items-center">
          <input
            className="input flex-1"
            placeholder="New stage…"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            onKeyDown={(e) =>
              e.key === "Enter" &&
              form.name.trim() &&
              !createMutation.isPending &&
              createMutation.mutate()
            }
          />
          <input
            type="color"
            className="input w-12 p-1 cursor-pointer"
            value={form.color || "#97A0B4"}
            onChange={(e) => setForm({ ...form, color: e.target.value })}
            title="Stage color"
          />
          <label className="flex items-center gap-1.5 text-xs text-mut-300 whitespace-nowrap">
            <input
              type="checkbox"
              checked={form.is_terminal}
              onChange={(e) => setForm({ ...form, is_terminal: e.target.checked })}
            />
            terminal
          </label>
          <button
            className="btn btn-primary"
            disabled={!form.name.trim() || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      )}

      <ul className="space-y-1.5">
        {stages.map((s, idx) =>
          isAdmin ? (
            <li key={s.id} className="panel-tight p-2.5 flex items-center gap-2">
              <input
                type="color"
                className="input w-9 h-8 p-1 cursor-pointer shrink-0"
                value={s.color ?? "#97A0B4"}
                onChange={(e) =>
                  updateMutation.mutate({ id: s.id, payload: { color: e.target.value } })
                }
                title="Color"
              />
              <input
                className="input flex-1"
                defaultValue={s.name}
                key={`${s.id}-${s.name}`}
                onBlur={(e) => {
                  const name = e.target.value.trim();
                  if (name && name !== s.name)
                    updateMutation.mutate({ id: s.id, payload: { name } });
                }}
              />
              <label
                className="flex items-center gap-1 text-xs text-mut-300 whitespace-nowrap"
                title="Terminal stage"
              >
                <input
                  type="checkbox"
                  checked={s.is_terminal}
                  onChange={(e) =>
                    updateMutation.mutate({ id: s.id, payload: { is_terminal: e.target.checked } })
                  }
                />
                term
              </label>
              <div className="flex flex-col">
                <button
                  className="text-mut-300 hover:text-white disabled:opacity-30 leading-none text-2xs"
                  disabled={idx === 0}
                  onClick={() => move(idx, -1)}
                  title="Move up"
                >
                  ▲
                </button>
                <button
                  className="text-mut-300 hover:text-white disabled:opacity-30 leading-none text-2xs"
                  disabled={idx === stages.length - 1}
                  onClick={() => move(idx, 1)}
                  title="Move down"
                >
                  ▼
                </button>
              </div>
              <button
                className="btn btn-ghost text-accent-pink"
                onClick={async () => {
                  if (
                    await confirmDialog({
                      title: `Delete the ${s.name} stage?`,
                      confirmLabel: "Delete stage",
                      danger: true,
                    })
                  )
                    deleteMutation.mutate(s.id);
                }}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ) : (
            <li
              key={s.id}
              className="flex items-center justify-between text-md py-1.5 px-2 rounded-md hover:bg-fg/[0.03]"
            >
              <span className="inline-flex items-center gap-2">
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: s.color ?? "#97A0B4" }}
                />
                {s.name}
                {s.is_terminal && (
                  <span className="chip bg-fg/[0.05] text-mut-300 text-2xs">terminal</span>
                )}
              </span>
              <span className="text-xs text-mut-300">pos {s.position}</span>
            </li>
          ),
        )}
      </ul>
      {!isAdmin && (
        <p className="text-xs text-mut-500 mt-3">Read-only — ask an admin to edit stages.</p>
      )}
    </section>
  );
}

// ─── Organizations (platform-admin) ───────────────────────────

function OrganizationsCard() {
  const qc = useQueryClient();
  const orgsQuery = useQuery({ queryKey: ["organizations"], queryFn: () => Organizations.list() });
  const [form, setForm] = useState({ name: "", slug: "", kind: "client" as "internal" | "client" });

  const createMutation = useMutation({
    mutationFn: () =>
      Organizations.create({ name: form.name.trim(), slug: form.slug.trim(), kind: form.kind }),
    onSuccess: () => {
      setForm({ name: "", slug: "", kind: "client" });
      qc.invalidateQueries({ queryKey: ["organizations"] });
    },
    onError: (e) => toast.err(apiErrorMessage(e)),
  });

  return (
    <section className="panel p-5">
      <h2 className="section-title mb-4">Organizations</h2>

      <div className="grid grid-cols-2 gap-2 mb-2">
        <input
          className="input"
          placeholder="Name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <input
          className="input"
          placeholder="slug"
          value={form.slug}
          onChange={(e) => setForm({ ...form, slug: e.target.value })}
        />
      </div>
      <div className="flex gap-2 mb-4">
        <select
          className="input flex-1"
          value={form.kind}
          onChange={(e) => setForm({ ...form, kind: e.target.value as "internal" | "client" })}
        >
          <option value="client">client</option>
          <option value="internal">internal</option>
        </select>
        <button
          className="btn btn-primary"
          disabled={!form.name.trim() || !form.slug.trim() || createMutation.isPending}
          onClick={() => createMutation.mutate()}
        >
          <Plus className="w-4 h-4" /> Add org
        </button>
      </div>

      <ul className="space-y-1.5">
        {(orgsQuery.data ?? []).map((o) => (
          <li
            key={o.id}
            className="flex items-center justify-between text-md py-1.5 px-2 rounded-md hover:bg-fg/[0.03]"
          >
            <span className={o.is_active ? "" : "line-through text-mut-300"}>
              {o.name} <span className="text-mut-300">· {o.slug}</span>
            </span>
            <span className="chip bg-fg/[0.05] text-mut-300 text-2xs">{o.kind}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
